/**
 * FERP Portal - Task Card for Workflow State View
 *
 * Compact card displaying a single task within a status column.
 * Read-only visualization — actions go through engine validation.
 *
 * FERP Principles:
 * - Dumb Frontend: All data from backend TaskFields fragment
 * - NO hardcoded values: Status/priority from generated enums
 * - NO drag-and-drop status changes: Engine validates transitions
 */

import {
  Card, Group, Stack, Text, Badge, Progress, Tooltip, ActionIcon, Menu,
} from '@mantine/core';
import {
  IconUser, IconClock, IconAlertTriangle, IconDots,
  IconPlayerPlay, IconCheck, IconPlayerPause, IconX, IconLock,
} from '@tabler/icons-react';
import {
  GqlTaskStatus,
  GqlTaskPriority,
  type TaskFieldsFragment,
} from '@/generated/graphql';
import { timeAgo } from './TaskDetailsContent';

const PRIORITY_CONFIG: Record<string, { color: string; label: string }> = {
  [GqlTaskPriority.CRITICAL]: { color: 'red', label: 'Critical' },
  [GqlTaskPriority.HIGH]: { color: 'orange', label: 'High' },
  [GqlTaskPriority.NORMAL]: { color: 'blue', label: 'Normal' },
  [GqlTaskPriority.LOW]: { color: 'gray', label: 'Low' },
};

/** Valid next transitions per status — engine enforces these, UI just shows buttons */
const ALLOWED_ACTIONS: Partial<Record<GqlTaskStatus, Array<{
  targetStatus: string;
  label: string;
  icon: React.ReactNode;
  color: string;
}>>> = {
  [GqlTaskStatus.READY]: [
    { targetStatus: 'start', label: 'Start', icon: <IconPlayerPlay size={14} />, color: 'cyan' },
  ],
  [GqlTaskStatus.ASSIGNED]: [
    { targetStatus: 'start', label: 'Start', icon: <IconPlayerPlay size={14} />, color: 'cyan' },
  ],
  [GqlTaskStatus.IN_PROGRESS]: [
    { targetStatus: 'complete', label: 'Complete', icon: <IconCheck size={14} />, color: 'green' },
    { targetStatus: 'pause', label: 'Pause', icon: <IconPlayerPause size={14} />, color: 'yellow' },
  ],
  [GqlTaskStatus.PAUSED]: [
    { targetStatus: 'resume', label: 'Resume', icon: <IconPlayerPlay size={14} />, color: 'cyan' },
  ],
  [GqlTaskStatus.BLOCKED]: [
    { targetStatus: 'start', label: 'Unblock & Start', icon: <IconPlayerPlay size={14} />, color: 'cyan' },
  ],
};

function formatDuration(ms: string | null | undefined): string | null {
  if (!ms) return null;
  const minutes = Math.round(parseInt(ms, 10) / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining > 0 ? `${hours}h ${remaining}m` : `${hours}h`;
}

interface TaskCardProps {
  task: TaskFieldsFragment;
  onAction?: (taskId: string, workflowInstanceId: string, action: string) => void;
  onClick?: (task: TaskFieldsFragment) => void;
}

export function TaskCard({ task, onAction, onClick }: TaskCardProps) {
  const priorityCfg = PRIORITY_CONFIG[task.priority] ?? { color: 'gray', label: task.priority };
  const actions = ALLOWED_ACTIONS[task.status] ?? [];
  const duration = formatDuration(task.durationMs ?? task.estimatedDurationMs);
  const hasDependencies = task.dependencies.length > 0;
  const hasProgress = task.progressPercent != null && task.progressPercent > 0;
  // DRR-297: Life-critical checkout lock indicator
  const isCheckedOut = !!task.checkedOutBy;

  return (
    <Card
      withBorder
      p="xs"
      radius="sm"
      className="selectable-card"
      onClick={() => onClick?.(task)}
      style={{
        borderLeft: `3px solid var(--mantine-color-${priorityCfg.color}-6)`,
      }}
    >
      <Stack gap={6}>
        {/* Title + overflow menu */}
        <Group justify="space-between" wrap="nowrap" gap={4}>
          <Text size="sm" fw={500} lineClamp={1} style={{ flex: 1 }}>
            {task.name}
          </Text>
          {actions.length > 0 && onAction && (
            <Menu position="bottom-end" withinPortal>
              <Menu.Target>
                <ActionIcon variant="subtle" size="xs" color="gray">
                  <IconDots size={14} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {actions.map((action) => (
                  <Menu.Item
                    key={action.targetStatus}
                    leftSection={action.icon}
                    color={action.color}
                    onClick={() => onAction(task.id, '', action.targetStatus)}
                  >
                    {action.label}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>

        {/* Description (if short) */}
        {task.description && (
          <Text size="xs" c="dimmed" lineClamp={1}>{task.description}</Text>
        )}

        {/* Progress bar */}
        {hasProgress && (
          <Tooltip label={`${task.progressPercent}% complete`}>
            <Progress
              value={task.progressPercent ?? 0}
              size="xs"
              color={task.progressPercent === 100 ? 'green' : 'cyan'}
            />
          </Tooltip>
        )}

        {/* Metadata row */}
        <Group gap={6} wrap="wrap">
          <Badge size="xs" variant="light" color={priorityCfg.color}>
            {priorityCfg.label}
          </Badge>

          {task.taskType && (
            <Badge size="xs" variant="dot">{task.taskType}</Badge>
          )}

          {hasDependencies && (
            <Tooltip label={`${task.dependencies.length} dependencies`}>
              <Badge size="xs" variant="outline" color="orange">
                <Group gap={2}>
                  <IconAlertTriangle size={10} />
                  <span>{task.dependencies.length} dep</span>
                </Group>
              </Badge>
            </Tooltip>
          )}

          {/* DRR-297: Life-critical task checkout indicator */}
          {isCheckedOut && (
            <Tooltip label={`In progress by: ${task.checkedOutBy}`}>
              <Badge size="xs" variant="filled" color="yellow">
                <Group gap={2}>
                  <IconLock size={10} />
                  <span>In Use</span>
                </Group>
              </Badge>
            </Tooltip>
          )}
        </Group>

        {/* Footer: assignee + time */}
        <Group gap={6} justify="space-between">
          {task.assignedActor?.id ? (
            <Tooltip label={`Assigned: ${task.assignedActor.displayName}`}>
              <Group gap={4}>
                <IconUser size={12} color="var(--mantine-color-dimmed)" />
                <Text size="xs" c="dimmed" lineClamp={1} maw={80}>
                  {task.assignedActor.displayName}
                </Text>
              </Group>
            </Tooltip>
          ) : (
            <Text size="xs" c="dimmed" fs="italic">Unassigned</Text>
          )}

          {duration && (
            <Tooltip label="Duration / Estimate">
              <Group gap={2}>
                <IconClock size={12} color="var(--mantine-color-dimmed)" />
                <Text size="xs" c="dimmed">{duration}</Text>
              </Group>
            </Tooltip>
          )}

          {!duration && task.createdAt && (
            <Text size="xs" c="dimmed">{timeAgo(task.createdAt)}</Text>
          )}
        </Group>
      </Stack>
    </Card>
  );
}
